import useToast from "@/contexts/toast-context";
import { buildErrorMessage } from "@/helpers/error-helpers";
import useLoading, { UseLoadingReturn } from "@/hooks/use-loading";
import { UpdateByIdProps } from "@/types/requests.types";

export type UpdateProps<T> = UpdateByIdProps<T>;

export type UseUpdateReturn<T, R = void> = {
  update: (props: UpdateProps<T>) => Promise<R>;
} & UseLoadingReturn;

export type UseUpdateProps<T, R = void> = {
  operation: (formData: T, _id: number) => Promise<R>;
};

const useUpdate = <T, R = void>({ operation }: UseUpdateProps<T, R>): UseUpdateReturn<T, R> => {
  const { isLoading, startLoading, endLoading, loadingIcon } = useLoading();
  const { showToast } = useToast();

  const update = async ({
    _id,
    formData,
    onSuccess,
    onError,
    successMessage = "",
    errorMessage = "",
  }: UpdateProps<T>): Promise<R> => {
    startLoading();
    try {
      const response = await operation(formData, _id);

      if (onSuccess) onSuccess();
      {
        successMessage !== "" && showToast({ text: successMessage, type: "success" });
      }
      return response;
    } catch (e) {
      const message = buildErrorMessage(e, errorMessage);
      showToast({ text: message, type: "error" });
      if (onError) onError();
      throw e;
    } finally {
      endLoading();
    }
  };

  return { isLoading, startLoading, endLoading, loadingIcon, update };
};

export default useUpdate;
